import {
    Box,
    Button,
    TextField,
    MenuItem,
    Collapse,
    useTheme,
    Grid2,
    Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import React, { useCallback, useEffect, useState } from 'react';
import { MultipleTextInput } from '../inputs/MultipleTextInput';
import { defaultAccountsFilterState } from '~/shared/const/accountsDefaults';
import { getFormattedList } from '~/shared/utils/googleConst';
import { ICountry, IFilterProps, IFilterState } from '~/shared/types/types';

export const FilterBar: React.FC<IFilterProps> = ({ filterColumns, onFilterApply, onFilterReset, open, toggleFilterBar, defaultState, activeFilters }) => {

    const getInitialState = useCallback(() => {
        const baseState = { ...((defaultState ?? defaultAccountsFilterState) as IFilterState) };
        if (!activeFilters) return baseState;

        const mergedState = { ...baseState };
        Object.keys(activeFilters).forEach(key => {
            const val = activeFilters[key];
            const colConfig = filterColumns.find(c => c.key === key);
            
            if (colConfig?.type === 'multi-text' && Array.isArray(val)) {
                mergedState[key] = val.join('\n');
            } else if (colConfig?.type === 'date' && typeof val === 'string') {
                mergedState[key] = val.split(' ')[0];
            } else {
                mergedState[key] = val;
            }
        });
        return mergedState;
    }, [activeFilters, defaultState, filterColumns]);

    const [filterState, setFilterState] = useState<IFilterState>(getInitialState);

    useEffect(() => {
        setFilterState(getInitialState());
    }, [activeFilters, getInitialState]); // Update local state when activeFilters changes (e.g. from URL/Storage or Reset)

    const theme = useTheme();
    const isValueEmpty = (value: any) => value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);
    const getInputSx = (isEmpty: boolean) => ({
        '& .MuiInputBase-input': {
            color: isEmpty ? theme.palette.text.secondary : theme.palette.text.primary,
        },
        '& .MuiInputBase-input::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
        },
        '& .MuiSelect-select': {
            color: isEmpty ? theme.palette.text.secondary : theme.palette.text.primary,
        },
    });

    const handleChange = useCallback(
        (field: keyof typeof filterState, value: any) => {
            setFilterState(prev => ({ ...prev, [field]: value }));
        }, [],
    );

    const handleTextArea = useCallback(
        (field: keyof typeof defaultAccountsFilterState, value: string) => {
            if (value.split('\n').length <= 50) {
                handleChange(field, value);
            }
        },
        [handleChange]
    );

    return (
        <Box width='100%'>
            <Collapse
                in={open}
                timeout={300}
                mountOnEnter
                unmountOnExit
                onExited={() => {
                    if (toggleFilterBar) toggleFilterBar(false);
                }}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    marginBottom: '2px',
                }}
            >
                <Box sx={{ px: 2, py: 2, backgroundColor: 'background.paper', minHeight: '100px' }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1fr 1fr',
                                md: '1fr 1fr 1fr',
                                lg: '1fr 1fr 1fr 1fr',
                            },
                            gap: 2,
                        }}
                    >
                        {filterColumns.map(field => {
                            const fieldValue = filterState[field.key];
                            const isEmpty = isValueEmpty(fieldValue);
                            return (
                            <Box key={field.key} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Typography variant="subtitle2" fontWeight={600}>{field.label}</Typography>
                                {field.type === 'geo' ? (
                                    (() => {
                                        const valueKey = (field.valueKey as keyof ICountry) || 'country_code';
                                        const selectedCountry = getFormattedList.find((c: ICountry) => c[valueKey] == filterState.geo
                                        );
                                        return (
                                            <TextField
                                                fullWidth
                                                select
                                                value={selectedCountry ? selectedCountry.id : ''}
                                                sx={getInputSx(!selectedCountry)}
                                                onChange={e => {
                                                    const country = getFormattedList.find((c: ICountry) => c.id === e.target.value);
                                                    if (country) {
                                                        handleChange('geo', country[valueKey]);
                                                    } else {
                                                        handleChange('geo', '');
                                                    }
                                                }}
                                            >
                                                <MenuItem value=''>Не выбрано</MenuItem>
                                                {getFormattedList.map((country: ICountry) => (
                                                    <MenuItem key={country.id} value={country.id}>
                                                        {country.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        );
                                    })()
                                ) : null}

                                {field.type === 'multi-text' ? (
                                    <MultipleTextInput
                                        minRows={1}
                                        maxRows={2}
                                        value={filterState[field.key] || ''}
                                        placeholder="До 50 строк"
                                        isError={false}
                                        onChange={e =>
                                            handleTextArea(field.key, e.target.value)
                                        }
                                        style={{
                                            backgroundColor: theme.palette.background.default,
                                            border: `1.5px solid ${theme.palette.divider}`,
                                            overflowY: 'auto',
                                            resize: 'none',
                                            color: isEmpty ? theme.palette.text.secondary : theme.palette.text.primary,
                                        }}
                                    />
                                ) : field.type === 'text' ? (
                                    <TextField
                                        fullWidth
                                        placeholder={field.label}
                                        sx={getInputSx(isEmpty)}
                                        value={filterState[field.key] || ''}
                                        onChange={e => {
                                            let value = e.target.value;
                                            if (field.key === 'customer_id') {
                                                value = value.replace(/\D/g, '');
                                            }
                                            handleChange(field.key, value);
                                        }}
                                    />
                                ) : field.type === 'select' ? (
                                    <TextField
                                        fullWidth
                                        select
                                        sx={getInputSx(isEmpty)}
                                        value={filterState[field.key] || ''}
                                        onChange={e => handleChange(field.key, e.target.value)}
                                    >
                                        <MenuItem value=''>Не выбрано</MenuItem>
                                        {field.options?.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : field.type === 'datetime' ? (
                                    <TextField
                                        fullWidth
                                        type='datetime-local'
                                        placeholder={field.label}
                                        sx={getInputSx(isEmpty)}
                                        value={filterState[field.key] || ''}
                                        onChange={e => handleChange(field.key, e.target.value)}
                                    />
                                ) : field.type === 'date' ? (
                                    <TextField
                                        fullWidth
                                        type='date'
                                        placeholder={field.label}
                                        sx={getInputSx(isEmpty)}
                                        value={filterState[field.key] || ''}
                                        onChange={e => handleChange(field.key, e.target.value)}
                                    />
                                ) : field.type === 'switch' ? (
                                    <TextField
                                        select
                                        fullWidth
                                        sx={getInputSx(isEmpty)}
                                        value={
                                            filterState[field.key] == '' ? '' :
                                                filterState[field.key] == 'true' ? 'true' : 'false'
                                        }
                                        onChange={e => handleChange(field.key, e.target.value)}
                                    >
                                        <MenuItem value=''>Не выбрано</MenuItem>
                                        <MenuItem value='true'>Да</MenuItem>
                                        <MenuItem value='false'>Нет</MenuItem>
                                    </TextField>
                                ) : null}
                            </Box>
                        )})}
                    </Box>
                    <Box display='flex' justifyContent='flex-end' mt={3}>
                        <Grid2 container justifyContent='flex-end' spacing={1}>
                            <Grid2>
                                <Button
                                    variant='outlined'
                                    color='secondary'
                                    startIcon={<RestartAltIcon />}
                                    onClick={() => {
                                        setFilterState((defaultState ?? defaultAccountsFilterState) as IFilterState);
                                        onFilterReset()
                                    }}
                                >
                                    Сбросить
                                </Button>
                            </Grid2>
                            <Grid2>
                                <Button variant='contained' onClick={() => onFilterApply(filterState)}>
                                    Применить
                                </Button>
                            </Grid2>
                        </Grid2>
                    </Box>
                </Box>
            </Collapse >
        </Box >
    );
};
